import api from "./base-api"

// Proxy to the ai-zendesk bot settings (backend: /api/v1/ai-zendesk/{glossaries,templates}).
// ai-zendesk has a single (production) environment, so these calls ignore X-Target-Environment.
const aiZendeskSettingsApi = api.injectEndpoints({
    endpoints: build => ({
        // game: ai-zendesk game key (clockmaker, toh, ...) -> {gameTag, terms: [{term, en, note}], updatedAt, updatedBy}
        getAiGlossary: build.query({
            query: (game) => ({url: `v1/ai-zendesk/glossaries/${game}`}),
            providesTags: (result, error, game) => [{type: "AiGlossary", id: game}]
        }),
        // replaces the whole term list of the game; an empty list turns the glossary off
        saveAiGlossary: build.mutation({
            query: ({game, terms}) => ({url: `v1/ai-zendesk/glossaries/${game}`, method: "PUT", body: {terms}}),
            invalidatesTags: (result, error, {game}) => [{type: "AiGlossary", id: game}]
        }),
        // -> [{template: {key, textEn, game, restrictionKind, restrictionReasons, updatedAt, updatedBy}, builtIn,
        //      translations: [{language, text, source, stale, updatedAt, updatedBy}]}]
        getAiTemplates: build.query({
            query: () => ({url: "v1/ai-zendesk/templates"}),
            providesTags: ["AiTemplates"]
        }),
        // new template: key [a-z0-9-] unique, English text, optional rule (restrictionKind + game + reasons)
        createAiTemplate: build.mutation({
            query: ({key, text, game, restrictionKind, reasons}) => ({
                url: "v1/ai-zendesk/templates",
                method: "POST",
                body: {key, text, game, restrictionKind, reasons}
            }),
            invalidatesTags: ["AiTemplates"]
        }),
        // only templates created from the panel (builtIn: false); removes its translations too
        deleteAiTemplate: build.mutation({
            query: ({key}) => ({url: `v1/ai-zendesk/templates/${key}`, method: "DELETE"}),
            invalidatesTags: ["AiTemplates"]
        }),
        // new English original: machine translations are redone on next use, manual ones become stale
        updateAiTemplate: build.mutation({
            query: ({key, text}) => ({url: `v1/ai-zendesk/templates/${key}`, method: "PUT", body: {text}}),
            invalidatesTags: ["AiTemplates"]
        }),
        // manual translation (source HUMAN) - used instead of the machine one
        saveAiTemplateTranslation: build.mutation({
            query: ({key, language, text}) => ({
                url: `v1/ai-zendesk/templates/${key}/translations/${language}`,
                method: "PUT",
                body: {text}
            }),
            invalidatesTags: ["AiTemplates"]
        }),
        // when the bot sends this template on its own: restrictionKind CHEATER (Hermes ban reason codes) | CHAT_MUTE (active chat mute)
        // | null (rule off), game (null = any)
        updateAiTemplateRule: build.mutation({
            query: ({key, game, restrictionKind, reasons}) => ({
                url: `v1/ai-zendesk/templates/${key}/restriction-rule`,
                method: "PUT",
                body: {game, restrictionKind, reasons}
            }),
            invalidatesTags: ["AiTemplates"]
        }),
        // drop a translation - ai-zendesk translates the template by machine again on next use
        deleteAiTemplateTranslation: build.mutation({
            query: ({key, language}) => ({url: `v1/ai-zendesk/templates/${key}/translations/${language}`, method: "DELETE"}),
            invalidatesTags: ["AiTemplates"]
        }),
        // knowledge from tickets: the quality judge turns an operator's reply into a general game rule (candidate),
        // a moderator approves it into a note the bot retrieves like a help-center article
        // -> [{id, ticketId, gameTag, answerId, questionEn, factEn, operatorQuote, improvementType, status, similar: [{id, title, note}], ...}]
        getAiKnowledgeCandidates: build.query({
            query: ({status = "NEW", game} = {}) => ({url: "v1/ai-zendesk/knowledge/candidates", params: game ? {status, game} : {status}}),
            providesTags: ["AiKnowledge"]
        }),
        getAiKnowledgePendingCount: build.query({
            query: () => ({url: "v1/ai-zendesk/knowledge/candidates/pending-count"}),
            providesTags: ["AiKnowledge"]
        }),
        // title/body: moderator's edits; omitted -> as the judge proposed. Returns the created note
        approveAiKnowledgeCandidate: build.mutation({
            query: ({id, title, body}) => ({url: `v1/ai-zendesk/knowledge/candidates/${id}/approve`, method: "POST", body: {title, body}}),
            invalidatesTags: ["AiKnowledge"]
        }),
        rejectAiKnowledgeCandidate: build.mutation({
            query: ({id}) => ({url: `v1/ai-zendesk/knowledge/candidates/${id}/reject`, method: "POST"}),
            invalidatesTags: ["AiKnowledge"]
        }),
        // -> [{id, gameTag, title, body, ticketId, createdBy, createdAt, updatedAt, updatedBy}]
        getAiKnowledgeNotes: build.query({
            query: ({game} = {}) => ({url: "v1/ai-zendesk/knowledge/notes", params: game ? {game} : undefined}),
            providesTags: ["AiKnowledge"]
        }),
        createAiKnowledgeNote: build.mutation({
            query: ({gameTag, title, body, ticketId}) => ({url: "v1/ai-zendesk/knowledge/notes", method: "POST", body: {gameTag, title, body, ticketId: ticketId ?? null}}),
            invalidatesTags: ["AiKnowledge"]
        }),
        updateAiKnowledgeNote: build.mutation({
            query: ({id, title, body}) => ({url: `v1/ai-zendesk/knowledge/notes/${id}`, method: "PUT", body: {title, body}}),
            invalidatesTags: ["AiKnowledge"]
        }),
        deleteAiKnowledgeNote: build.mutation({
            query: ({id}) => ({url: `v1/ai-zendesk/knowledge/notes/${id}`, method: "DELETE"}),
            invalidatesTags: ["AiKnowledge"]
        })
    })
})

export const {
    useGetAiGlossaryQuery,
    useSaveAiGlossaryMutation,
    useGetAiTemplatesQuery,
    useCreateAiTemplateMutation,
    useDeleteAiTemplateMutation,
    useUpdateAiTemplateMutation,
    useSaveAiTemplateTranslationMutation,
    useUpdateAiTemplateRuleMutation,
    useDeleteAiTemplateTranslationMutation,
    useGetAiKnowledgeCandidatesQuery,
    useGetAiKnowledgePendingCountQuery,
    useApproveAiKnowledgeCandidateMutation,
    useRejectAiKnowledgeCandidateMutation,
    useGetAiKnowledgeNotesQuery,
    useCreateAiKnowledgeNoteMutation,
    useUpdateAiKnowledgeNoteMutation,
    useDeleteAiKnowledgeNoteMutation
} = aiZendeskSettingsApi

export default aiZendeskSettingsApi
